// client/src/components/scheduling/DeleteDayAvailabilityModal.js
import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { formatUtcToLocal } from '../../utils/format'
import services from '../../services'
import './DeleteDayAvailabilityModal.css'
import toastr from 'toastr'

const DATE_FMT = 'EEEE, MMM d, yyyy'

export default function DeleteDayAvailabilityModal({
  isOpen,
  onClose,
  matchDate,
  leagueId,
  divisionId,
  opponentName,
}) {
  const [isDeleting, setIsDeleting] = useState(false)
  const params = useParams()

  // Prefer passed props; fall back to URL params if not provided
  const effectiveLeagueId =
    leagueId ?? (params.leagueId ? parseInt(params.leagueId, 10) : null)
  const effectiveDivisionId =
    divisionId ?? (params.divisionId ? parseInt(params.divisionId, 10) : null)

  const formattedDate = React.useMemo(() => {
    if (!matchDate) return ''
    try {
      return formatUtcToLocal(matchDate, DATE_FMT)
    } catch {
      return matchDate
    }
  }, [matchDate])

  if (!isOpen) return null

  const handleDelete = async () => {
    if (isDeleting) return

    setIsDeleting(true)
    try {
      // Resolve league and division IDs if missing
      let leagueToUse = effectiveLeagueId
      let divisionToUse = effectiveDivisionId

      if (!leagueToUse && params.leagueId) {
        leagueToUse = parseInt(params.leagueId, 10)
      }

      if (!leagueToUse) {
        toastr.error('Missing league or division information')
        return
      }

      if (!divisionToUse) {
        try {
          const divResp = await services.league.getMyDivision(leagueToUse)
          if (divResp.success && divResp.data?.division_id) {
            divisionToUse = divResp.data.division_id
          }
        } catch {
          // ignore division lookup error, will handle below
        }
      }

      if (!divisionToUse) {
        toastr.error('Missing league or division information')
        return
      }

      const result = await services.availability.deleteAvailabilityForDay(
        matchDate,
        leagueToUse,
        divisionToUse,
      )

      if (result.success) {
        onClose(true) // Pass true to indicate deletion occurred
      }
    } catch (error) {
      console.error('Error deleting day availability:', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleSkip = () => {
    onClose(false) // Pass false to indicate no deletion
  }

  return (
    <div className="delete-day-availability-modal-overlay" onClick={handleSkip}>
      <div
        className="delete-day-availability-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="delete-day-availability-modal-header">
          <h3>Remove Rest of Day Availability?</h3>
          <button
            className="delete-day-availability-close-button"
            onClick={handleSkip}
          >
            ×
          </button>
        </div>

        <div className="delete-day-availability-modal-body">
          <div className="availability-deletion-info">
            <div className="match-info">
              <h4>Match Scheduled</h4>
              <p className="match-details">
                {opponentName && `vs ${opponentName} on `}
                <strong>{formattedDate}</strong>
              </p>
            </div>

            <div className="deletion-prompt">
              <p className="prompt-text">
                Since you've scheduled a match for this day, would you like to
                remove your remaining availability for{' '}
                <strong>{formattedDate}</strong>?
              </p>
              <p className="prompt-subtext text-muted">
                This will help avoid scheduling conflicts and let other players
                know you're not available for additional matches on this day.
              </p>
            </div>
          </div>
        </div>

        <div className="delete-day-availability-modal-footer">
          <button
            className="btn btn-secondary"
            onClick={handleSkip}
            disabled={isDeleting}
          >
            Keep My Availability
          </button>
          <button
            className="btn btn-primary"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Removing...' : 'Remove Rest of Day'}
          </button>
        </div>
      </div>
    </div>
  )
}
